import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import "./Skeleton.css";

type CSSDimension = string | number;
type SkeletonTone = "theme" | "soft" | "neutral" | "alt" | "contrast";

export type DonutChartSkeletonRing = {
  innerRadius: number;
  outerRadius: number;
  segments?: number;
  offset?: number;
};

export type DonutChartSkeletonProps = {
  width?: CSSDimension;
  height?: CSSDimension;
  tone?: SkeletonTone;
  className?: string;
  style?: CSSProperties;
  rings?: DonutChartSkeletonRing[];
  legendRows?: number;
  titleWidth?: CSSDimension;
  valueWidth?: CSSDimension;
};

function toCssDimension(value?: CSSDimension): string | undefined {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
}

function buildRingDashPattern(radius: number, segments: number): string {
  const circumference = 2 * Math.PI * radius;
  const safeSegments = Math.max(2, segments);
  const visible = circumference / safeSegments * 0.7;
  const gap = circumference / safeSegments - visible;

  return `${visible} ${Math.max(gap, visible * 0.2)}`;
}

const defaultRings: DonutChartSkeletonRing[] = [
  { innerRadius: 40, outerRadius: 110, segments: 4, offset: 0.15 },
];

export default function DonutChartSkeleton({
  width = "100%",
  height = 240,
  tone = "soft",
  className = "",
  style,
  rings = defaultRings,
  legendRows = 4,
  titleWidth = "42%",
  valueWidth = "24%",
}: DonutChartSkeletonProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      setMeasuredWidth(node.getBoundingClientRect().width);
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const wrapperStyle: CSSProperties = {
    width: toCssDimension(width),
    height: toCssDimension(height),
    ...style,
  };

  const numericHeight = typeof height === "number" ? height : 240;
  const chartHeight = Math.max(140, numericHeight);
  const chartWidth = measuredWidth > 0 ? measuredWidth : 320;
  const squareSize = Math.max(120, Math.min(chartWidth, chartHeight));
  const chartCenter = squareSize / 2;
  const maxOuterRadius = useMemo(
    () => Math.max(...rings.map((ring) => ring.outerRadius)),
    [rings],
  );
  const scale = maxOuterRadius > 0 ? (squareSize * 0.42) / maxOuterRadius : 1;

  const classes = [
    "domino-chart-skeleton",
    "domino-chart-skeleton--donut",
    `domino-skeleton--tone-${tone}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div aria-hidden="true" ref={wrapperRef} className={classes} style={wrapperStyle}>
      <div className="domino-chart-skeleton__canvas domino-chart-skeleton__canvas--centered">
        <svg
          className="domino-chart-skeleton__svg"
          viewBox={`0 0 ${squareSize} ${squareSize}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {rings.map((ring, index) => {
            const radius = ((ring.innerRadius + ring.outerRadius) / 2) * scale;
            const strokeWidth = Math.max(8, (ring.outerRadius - ring.innerRadius) * scale);
            const dashArray = buildRingDashPattern(radius, ring.segments || 4);
            const dashOffset = -2 * Math.PI * radius * (ring.offset || index * 0.08);

            return (
              <circle
                key={`ring-${index}`}
                cx={chartCenter}
                cy={chartCenter}
                r={radius}
                className={`domino-chart-skeleton__ring domino-chart-skeleton__ring--${index % 3}`}
                style={{
                  strokeWidth,
                  strokeDasharray: dashArray,
                  strokeDashoffset: dashOffset,
                }}
              />
            );
          })}
        </svg>
      </div>

      <div className="domino-chart-skeleton__legend">
        {Array.from({ length: legendRows }).map((_, index) => (
          <div key={`legend-${index}`} className="domino-chart-skeleton__legend-row">
            <span className="domino-chart-skeleton__legend-bullet" />
            <span
              className="domino-chart-skeleton__legend-label"
              style={{
                width:
                  index === legendRows - 1 && legendRows > 1
                    ? "54%"
                    : toCssDimension(titleWidth),
              }}
            />
            <span
              className="domino-chart-skeleton__legend-value"
              style={{ width: toCssDimension(valueWidth) }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
